<template>
    <div>
        <div
            class="travel-segment-group"
            v-for="(segment, index) in activeRedemption.travel_segments"
            :key="segment"
        >
            <h6
                class="text-slate-800 dark:text-slate-100 text-sm font-medium mb-3 flex items-center justify-between"
                v-if="segments.length > 1"
            >
                <span>Segment {{ index + 1 }}</span>
                <button
                    class="flex gap-2 text-sm font-medium text-slate-800 dark:text-slate-100 hover:text-slate-500 dark:text-slate-400"
                    v-if="index > 0"
                    @click="removeSegment(index)"
                >
                    <Icon icon="trash" />
                    Remove
                </button>
            </h6>
            <div
                class="grid grid-cols-2 gap-4 mb-8"
                v-if="redemptionType.slug === 'flight'"
            >
                <div class="col-span-1">
                    <AirportSearch
                        label="Origin"
                        placeholder="Origin"
                        :currentAirport="segment.origin"
                        :hasError="!!tripFormErrors['origin']"
                        @airportChanged="updateSegmentOrigin(index, $event)"
                    />
                </div>
                <div class="col-span-1">
                    <AirportSearch
                        label="Destination"
                        placeholder="Destination"
                        :currentAirport="segment.destination"
                        :hasError="!!tripFormErrors['destination']"
                        @airportChanged="
                            updateSegmentDestination(index, $event)
                        "
                    />
                </div>
            </div>
            <div
                class="grid grid-cols-2 gap-4 mb-8"
                v-if="redemptionType.slug === 'train'"
            >
                <div class="col-span-1">
                    <Input
                        label="Origin"
                        placeholder="Origin"
                        :id="`origin-${index}`"
                        name="origin"
                        v-model:inputValue="segment.origin"
                        :errors="tripFormErrors"
                        :options="{ inputClass: 'h-10' }"
                    />
                </div>
                <div class="col-span-1">
                    <Input
                        label="Destination"
                        placeholder="Destination"
                        :id="`destination-${index}`"
                        name="destination"
                        v-model:inputValue="segment.destination"
                        :errors="tripFormErrors"
                        :options="{ inputClass: 'h-10' }"
                    />
                </div>
            </div>
            <div class="grid grid-cols-2 gap-4 mb-6">
                <div class="col-span-1">
                    <Input
                        :label="travelNumberLabel"
                        placeholder="E.g., UA1234"
                        :id="`travel-number-${index}`"
                        v-model:inputValue="segment.travel_number"
                        :options="{ inputClass: 'h-10' }"
                        @update:inputValue="() => updateSegment(segment, index)"
                    />
                    <Transition name="fade">
                        <div
                            v-if="
                                segment.travel_number &&
                                segment.travel_number
                                    .trim()
                                    .toUpperCase()
                                    .startsWith('WN')
                            "
                            class="flex pt-2 items-start gap-1 self-stretch text-slate-500 dark:text-slate-400"
                        >
                            <Icon icon="circle-info" class="w-5 h-5" />
                            <span class="text-xs font-normal leading-4">
                                This appears to be a Southwest flight. You'll
                                have the option to apply a Companion Pass in the
                                next step.
                            </span>
                        </div>
                    </Transition>
                </div>
            </div>
        </div>

        <button
            class="btn btn-secondary block !w-full mb-8"
            @click="addSegment()"
        >
            <Icon icon="plus" />
            Add Segment
        </button>
    </div>
</template>
<script setup>
import { computed, ref, watch } from 'vue';
import { useTripsStore } from '@/store/trips';
import Icon from '@/components/ui/icon/icon.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import AirportSearch from '@/components/trips/redemptions/airport-search.vue';
import { storeToRefs } from 'pinia';
const tripsStore = useTripsStore();

const props = defineProps({
    travelNumberLabel: {
        type: String,
        default() {
            return 'Travel number';
        },
    },
    segments: {
        type: Array,
        default() {
            return [];
        },
    },
});

const emit = defineEmits(['segmentsUpdated']);

const { activeRedemption, redemptionTypes, tripFormErrors } =
    storeToRefs(tripsStore);

const redemptionType = computed(() => {
    return redemptionTypes.value.find(
        (redemptionType) =>
            redemptionType.id === activeRedemption.value.redemption_type_id
    );
});

const checkSouthwestFlight = (segment) => {
    const isWN =
        segment.travel_number &&
        segment.travel_number.trim().toUpperCase().startsWith('WN');

    if (tripsStore.activeRedemption) {
        tripsStore.activeRedemption.isSouthwestFlight = isWN;
    }

    return isWN;
};

watch(
    () => props.segments,
    (newSegments) => {
        if (newSegments && newSegments.length > 0) {
            const hasSouthwestFlight = newSegments.some(
                (segment) =>
                    segment.travel_number &&
                    segment.travel_number.trim().toUpperCase().startsWith('WN')
            );

            if (tripsStore.activeRedemption) {
                tripsStore.activeRedemption.isSouthwestFlight =
                    hasSouthwestFlight;
            }
        }
    },
    { deep: true, immediate: true }
);

const updateSegment = (segment, index) => {
    checkSouthwestFlight(segment);

    emit('segmentsUpdated', props.segments);
};

const addSegment = () => {
    activeRedemption.value.travel_segments.push({
        origin: null,
        origin_name: null,
        destination: null,
        destination_name: null,
        travel_number: null,
    });
    emit('segmentsUpdated', activeRedemption.value.travel_segments);
};

const removeSegment = (index) => {
    activeRedemption.value.travel_segments.splice(index, 1);
    emit('segmentsUpdated', activeRedemption.value.travel_segments);
};

const updateSegmentOrigin = (index, airport) => {
    if (airport) {
        activeRedemption.value.travel_segments[index].origin = airport.code;
        activeRedemption.value.travel_segments[index].origin_name = airport.name;
    } else {
        activeRedemption.value.travel_segments[index].origin = null;
        activeRedemption.value.travel_segments[index].origin_name = null;
    }
    delete tripFormErrors.value['origin'];
    emit('segmentsUpdated', activeRedemption.value.travel_segments);
};

const updateSegmentDestination = (index, airport) => {
    if (airport) {
        activeRedemption.value.travel_segments[index].destination = airport.code;
        activeRedemption.value.travel_segments[index].destination_name = airport.name;
    } else {
        activeRedemption.value.travel_segments[index].destination = null;
        activeRedemption.value.travel_segments[index].destination_name = null;
    }
    delete tripFormErrors.value['destination'];
    emit('segmentsUpdated', activeRedemption.value.travel_segments);
};
</script>
